export function formatDateTime(input?: string | number | Date): string {
  if (!input) return '';
  const d = new Date(input);
  if (isNaN(d.getTime())) return '';
  const pad = (n: number) => n.toString().padStart(2, '0');
  const dd = pad(d.getDate());
  const mm = pad(d.getMonth() + 1);
  const yy = d.getFullYear().toString().slice(-2);
  const hh = pad(d.getHours());
  const min = pad(d.getMinutes());
  return `${dd}-${mm}-${yy} ${hh}:${min}`;
}

export function formatDate(input?: string | number | Date): string {
  if (!input) return '';
  const d = new Date(input);
  if (isNaN(d.getTime())) return '';
  const pad = (n: number) => n.toString().padStart(2, '0');
  const dd = pad(d.getDate());
  const mm = pad(d.getMonth() + 1);
  const yy = d.getFullYear().toString().slice(-2);
  return `${dd}-${mm}-${yy}`;
}

/** שעה מקומית HH:mm (כמו ב-formatDateTime, בלי תאריך) */
export function formatTime(input?: string | number | Date): string {
  if (!input) return '';
  const d = new Date(input);
  if (isNaN(d.getTime())) return '';
  const pad = (n: number) => n.toString().padStart(2, '0');
  return `${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** פורמט ידידותי לאקסל: DD/MM/YYYY␠␠HH:mm:ss (שני רווחים בין תאריך לשעה) */
export function formatDateTimeExcel(input?: string | number | Date | null): string {
  if (input === null || input === undefined || input === '') return '';
  const d = new Date(input);
  if (isNaN(d.getTime())) return '';
  const pad = (n: number) => n.toString().padStart(2, '0');
  const dd = pad(d.getDate());
  const mm = pad(d.getMonth() + 1);
  const yyyy = d.getFullYear();
  const hh = pad(d.getHours());
  const min = pad(d.getMinutes());
  const ss = pad(d.getSeconds());
  return `${dd}/${mm}/${yyyy}  ${hh}:${min}:${ss}`;
}

/**
 * המרה ל-ISO לשמירה במערכת: תומך בפורמט אקסל, ב-ISO ובפורמטים ש-Date מפרש.
 * קבצי ייצוא ישנים עם עמודות "(ISO)" נשארים תואמים דרך הקריאות ב-handleImport.
 */
export function parseFlexibleDateToIso(v: unknown): string {
  if (v === null || v === undefined) return '';
  const s = String(v).trim();
  if (!s) return '';

  const dt = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})\s+(\d{1,2}):(\d{1,2}):(\d{1,2})$/);
  if (dt) {
    const day = Number(dt[1]);
    const month = Number(dt[2]);
    const year = Number(dt[3]);
    const h = Number(dt[4]);
    const m = Number(dt[5]);
    const sec = Number(dt[6]);
    const d = new Date(year, month - 1, day, h, m, sec);
    return Number.isNaN(d.getTime()) ? '' : d.toISOString();
  }

  const dOnly = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (dOnly) {
    const day = Number(dOnly[1]);
    const month = Number(dOnly[2]);
    const year = Number(dOnly[3]);
    const d = new Date(year, month - 1, day, 0, 0, 0);
    return Number.isNaN(d.getTime()) ? '' : d.toISOString();
  }

  const dotOnly = s.match(/^(\d{1,2})\.(\d{1,2})\.(\d{4})(?:\s+(\d{1,2}):(\d{2})(?::(\d{2}))?)?$/);
  if (dotOnly) {
    const day = Number(dotOnly[1]);
    const month = Number(dotOnly[2]);
    const year = Number(dotOnly[3]);
    const h = Number(dotOnly[4] ?? 0);
    const m = Number(dotOnly[5] ?? 0);
    const sec = Number(dotOnly[6] ?? 0);
    const d = new Date(year, month - 1, day, h, m, sec);
    return Number.isNaN(d.getTime()) ? '' : d.toISOString();
  }

  const dashOnly = s.match(/^(\d{1,2})-(\d{1,2})-(\d{4})(?:\s+(\d{1,2}):(\d{2})(?::(\d{2}))?)?$/);
  if (dashOnly) {
    const day = Number(dashOnly[1]);
    const month = Number(dashOnly[2]);
    const year = Number(dashOnly[3]);
    const h = Number(dashOnly[4] ?? 0);
    const m = Number(dashOnly[5] ?? 0);
    const sec = Number(dashOnly[6] ?? 0);
    const d = new Date(year, month - 1, day, h, m, sec);
    return Number.isNaN(d.getTime()) ? '' : d.toISOString();
  }

  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? '' : d.toISOString();
}

/** תחילת יום מקומי כ-ISO — תואם לקריאות שירות (afterDate) */
export function toStartOfDayIso(value?: Date | string | null): string | undefined {
  if (value == null || value === '') return undefined;
  let parsed: Date;
  if (value instanceof Date) {
    parsed = new Date(value.getTime());
  } else {
    const flex = parseFlexibleDateToIso(value);
    parsed = new Date(flex || String(value));
  }
  if (Number.isNaN(parsed.getTime())) return undefined;
  parsed.setHours(0, 0, 0, 0);
  return parsed.toISOString();
}

/** סוף יום מקומי כ-ISO — תואם לקריאות שירות (beforeDate) */
export function toEndOfDayIso(value?: Date | string | null): string | undefined {
  if (value == null || value === '') return undefined;
  let parsed: Date;
  if (value instanceof Date) {
    parsed = new Date(value.getTime());
  } else {
    const flex = parseFlexibleDateToIso(value);
    parsed = new Date(flex || String(value));
  }
  if (Number.isNaN(parsed.getTime())) return undefined;
  parsed.setHours(23, 59, 59, 999);
  return parsed.toISOString();
}

/** תחילת יום מקומי — לסינון «מתאריך» בדוחות */
export function normalizeReportFilterFromDate(value?: Date | null): Date | null {
  if (!value) return null;
  const parsed = new Date(value.getTime());
  if (Number.isNaN(parsed.getTime())) return null;
  parsed.setHours(0, 0, 0, 0);
  return parsed;
}

/** סוף יום מקומי — «עד תאריך» כולל את כל היום */
export function normalizeReportFilterToDate(value?: Date | null): Date | null {
  if (!value) return null;
  const parsed = new Date(value.getTime());
  if (Number.isNaN(parsed.getTime())) return null;
  parsed.setHours(23, 59, 59, 999);
  return parsed;
}

/** השוואת תאריכי סינון לפי יום קלנדרי (לא לפי שעה) */
export function reportFilterDatesEqual(a: Date | null, b: Date | null): boolean {
  if (!a && !b) return true;
  if (!a || !b) return false;
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

